import { useState } from "react";
import { Button } from "@/components/ui/button";
import { FileSpreadsheet, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

/**
 * ExportXlsxButton — Télécharge un fichier .xlsx via un endpoint /api/exports-xlsx/*
 *
 * Props:
 * - endpoint: chemin RELATIF sous /api (ex: "/exports-xlsx/athletes")
 * - params:   objet clé/valeur des query params (undefined et "all" sont ignorés)
 * - filename: nom de fichier suggéré côté client (fallback si le header serveur manque)
 * - label:    texte du bouton (défaut: "Exporter Excel")
 * - variant / size / className: props passées au Button
 * - authRequired: si true, inclut le Bearer token (federation cockpit)
 * - testid:   data-testid personnalisé
 */
export function ExportXlsxButton({
  endpoint,
  params = {},
  filename = "export.xlsx",
  label = "Exporter Excel",
  variant = "outline",
  size = "sm",
  className = "",
  authRequired = false,
  testid = "export-xlsx-button",
  disabled = false,
}) {
  const [loading, setLoading] = useState(false);

  const buildUrl = () => {
    const clean = {};
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v === undefined || v === null || v === "" || v === "all" || v === "__all__") return;
      clean[k] = v;
    });
    const qs = new URLSearchParams(clean).toString();
    return `${API}${endpoint}${qs ? `?${qs}` : ""}`;
  };

  const extractFilename = (contentDisposition, fallback) => {
    if (!contentDisposition) return fallback;
    // Content-Disposition: attachment; filename="AITO_athletes_2026-09-19.xlsx"
    const match = contentDisposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
    return match ? decodeURIComponent(match[1]) : fallback;
  };

  const handleClick = async () => {
    setLoading(true);
    try {
      const headers = {};
      if (authRequired) {
        const token = localStorage.getItem("aito_token");
        if (token) headers.Authorization = `Bearer ${token}`;
      }
      const res = await fetch(buildUrl(), { headers });
      if (!res.ok) {
        let detail = "Export impossible.";
        try {
          const j = await res.json();
          detail = j.detail || detail;
        } catch { /* ignore */ }
        throw new Error(detail);
      }
      const blob = await res.blob();
      const dispositionName = extractFilename(res.headers.get("Content-Disposition"), filename);

      // Déclenche le téléchargement
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = dispositionName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Nettoyage différé — certains navigateurs invalident trop tôt
      setTimeout(() => window.URL.revokeObjectURL(url), 2000);
      toast.success("Export Excel généré.");
    } catch (e) {
      toast.error(e?.message || "Export impossible.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      className={className}
      onClick={handleClick}
      disabled={loading || disabled}
      data-testid={testid}
      title="Télécharger la liste filtrée au format Excel"
    >
      {loading ? (
        <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
      ) : (
        <FileSpreadsheet className="h-4 w-4 mr-1.5" />
      )}
      {loading ? "Génération…" : label}
    </Button>
  );
}

export default ExportXlsxButton;
